/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: FirewallServiceModal.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Modal component for creating/editing firewall services
----------------------------------------------------------------*/

import { useState, useEffect, ReactNode } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import Modal from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { createFirewallService, updateFirewallService } from '../api/firewallServicesApi';
import type { FirewallService, FirewallServiceFormData } from '../types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
  required?: boolean;
  alignTop?: boolean;
};

const FormField = ({ id, label, tooltip, children, required, alignTop = false }: FormFieldProps) => (
  <div className={`flex gap-4 ${alignTop ? 'items-start' : 'items-center'}`}>
    <SimpleTooltip content={tooltip}>
      <Label
        htmlFor={id}
        className={`text-sm font-medium text-foreground w-36 flex-shrink-0 ${alignTop ? 'pt-2' : 'h-10 flex items-center'}`}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
    </SimpleTooltip>
    <div className="flex-1">
      {children}
    </div>
  </div>
);

interface FirewallServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  service?: FirewallService | null;
}

export function FirewallServiceModal({
  isOpen,
  onClose,
  onSuccess,
  service,
}: FirewallServiceModalProps) {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FirewallServiceFormData>({
    name: '',
    protocol: 'tcp',
    port: '',
    description: '',
    enabled: true,
  });

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        protocol: service.protocol,
        port: service.port,
        description: service.description || '',
        enabled: service.enabled,
      });
    } else {
      setFormData({
        name: '',
        protocol: 'tcp',
        port: '',
        description: '',
        enabled: true,
      });
    }
  }, [service, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (service) {
        await updateFirewallService(service.id, formData);
        showSuccess(t('firewallServices.messages.updateSuccess'));
      } else {
        await createFirewallService(formData);
        showSuccess(t('firewallServices.messages.createSuccess'));
      }
      onSuccess();
      onClose();
    } catch (error) {
      showError(error.message || t('firewallServices.messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={service ? t('firewallServices.modal.editTitle') : t('firewallServices.modal.createTitle')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* L�nea 1: Service Name */}
        <FormField
          id="name"
          label={t('firewallServices.form.name')}
          tooltip={t('firewallServices.tooltips.name')}
          required
        >
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('firewallServices.form.namePlaceholder')}
            required
            disabled={loading}
          />
        </FormField>

        {/* L�nea 2: Protocol */}
        <FormField
          id="protocol"
          label={t('firewallServices.form.protocol')}
          tooltip={t('firewallServices.tooltips.protocol')}
          required
        >
          <Select
            value={formData.protocol}
            onValueChange={(value: FirewallServiceFormData['protocol']) =>
              setFormData({ ...formData, protocol: value })
            }
          >
            <SelectTrigger className="w-full" disabled={loading}>
              <SelectValue placeholder={t('firewallServices.form.protocolPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tcp">TCP</SelectItem>
              <SelectItem value="udp">UDP</SelectItem>
              <SelectItem value="tcp/udp">TCP/UDP</SelectItem>
              <SelectItem value="icmp">ICMP</SelectItem>
              <SelectItem value="icmpv6">ICMPv6</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* L�nea 3: Port */}
        <FormField
          id="port"
          label={t('firewallServices.form.port')}
          tooltip={t('firewallServices.tooltips.port')}
          required
        >
          <Input
            id="port"
            value={formData.port}
            onChange={(e) => setFormData({ ...formData, port: e.target.value })}
            placeholder={t('firewallServices.form.portPlaceholder')}
            required
            disabled={loading}
          />
        </FormField>

        {/* L�nea 4: Description */}
        <FormField
          id="description"
          label={t('firewallServices.form.description')}
          tooltip={t('firewallServices.tooltips.description')}
        >
          <Input
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('firewallServices.form.descriptionPlaceholder')}
            disabled={loading}
          />
        </FormField>

        {/* L�nea 5: Enabled */}
        <FormField
          id="enabled"
          label={t('firewallServices.form.enabled')}
          tooltip={t('firewallServices.tooltips.enabled')}
        >
          <div className="flex items-center">
            <Toggle
              value={formData.enabled ?? true}
              onChange={(enabled) => setFormData({ ...formData, enabled })}
              label=""
              disabled={loading}
              trueLabel={t('common.yes')}
              falseLabel={t('common.no')}
            />
          </div>
        </FormField>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading
              ? t('common.saving')
              : service
                ? t('common.update')
                : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
